import React, { useState, useEffect, useRef } from "react";
import { FormControl, Button, Form } from "react-bootstrap";
import { FaSearch, FaTimes } from "react-icons/fa";
import api from "../../api";
import { Modal } from "react-bootstrap";
import ReactPlayer from "react-player";
import ContainerLoader from "../loader/container-loader";
import { trackEvent } from "../../utils/gaTracking";
import { useSelector } from "react-redux";

const KfmSearchBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [activeEpisode, setActiveEpisode] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const searchRef = useRef(null);
  const authState = useSelector((state) => state.authUser);
  const user_id = authState?.profile?.user_id;
  const user_email = authState?.profile?.email;

  // Fetch results
  const fetchResults = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setResults([]);
      return;
    }
    try {
      setIsFetching(true);
      const request = { resource: "api/searchkfm" };
      const response = await api.searchfliphub.addDetails(request, {
        query: searchQuery,
      });

      if (response.status === 200) {
        setResults(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching KFM search results:", error);
    } finally {
      setIsFetching(false);
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    fetchResults(query);
  };

  // close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // highlight matched text
  const highlightText = (text, search) => {
    if (!search) return text;
    const regex = new RegExp(`(${search})`, "gi");
    return text.replace(regex, `<mark style="background: yellow">$1</mark>`);
  };

  return (
    <>
      {/* Search Icon */}
      <div className="position-relative pe-3">
        <Button
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          variant="outline-secondary"
          className="navHeader navSearchBar"
        >
          <FaSearch />
        </Button>
      </div>

      {/* Search Box */}
      {isOpen && (
        <div
          className="position-absolute top-100 start-0 w-100 bg-white shadow p-3 rounded z-3"
          ref={searchRef}
        >
          <Form onSubmit={handleSearchSubmit} className="d-flex">
            <FormControl
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search KFM sessions, Audios ..."
            />
            {query && (
              <Button
                variant="light"
                className="ms-2"
                onClick={() => setQuery("")}
                disabled={isFetching}
              >
                <FaTimes />
              </Button>
            )}
            <Button
              onClick={handleSearchSubmit}
              variant="primary"
              className="ms-2"
              disabled={isFetching}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                setIsOpen(false);
                setResults([]);
                setQuery("");
              }}
              variant="primary"
              className="ms-2"
              disabled={isFetching}
            >
              Cancel
            </Button>
          </Form>

          <div className="pt-3">
            {isFetching ? (
              <p className="text-muted">Fetching...</p>
            ) : query !== "" && results?.length === 0 ? (
              <p className="text-muted text-center">
                No Results Found! Please refine your query.
              </p>
            ) : null}
          </div>

          {/* Results */}
          <ul
            className="list-group"
            style={{ maxHeight: "300px", overflow: "auto" }}
          >
            {results?.map((episode) => (
              <li
                key={episode.id}
                className="list-group-item list-group-item-action"
              >
                <div className="d-flex align-items-center">
                  <img
                    src={episode.imageurl}
                    alt="preview"
                    className="me-3"
                    style={{ width: "40px", height: "40px" }}
                  />

                  <div>
                    <p
                      className="fw-bold mb-1"
                      dangerouslySetInnerHTML={{
                        __html: highlightText(
                          episode.episode_title || "",
                          query
                        ),
                      }}
                    />
                    <p
                      className="fw-bold mb-1"
                      dangerouslySetInnerHTML={{
                        __html: highlightText(
                          episode.episode_name || "",
                          query
                        ),
                      }}
                    />
                    <p
                      className="small"
                      dangerouslySetInnerHTML={{
                        __html: highlightText(
                          episode.episode_description || "",
                          query
                        ),
                      }}
                    />

                    {/* ✓ Audio Play Button */}
                    {episode.audiourl && (
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => {
                          setActiveEpisode(episode);
                          setShowModal(true);
                          trackEvent(
                            "kfm_search_audio_play",
                            "KFM Audio",
                            episode.episode_name,
                            1,
                            user_id,
                            user_email
                          );
                        }}
                      >
                        Play Audio
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* AUDIO MODAL */}
      <Modal
        show={showModal}
        centered
        fullscreen
        onHide={() => {
          setShowModal(false);
          setActiveEpisode(null);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{activeEpisode?.episode_name}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {activeEpisode?.audiourl ? (
            <ReactPlayer
              url={activeEpisode.audiourl}
              controls
              width="100%"
              height="100%"
            />
          ) : (
            <p className="text-danger">Audio not available.</p>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default KfmSearchBar;
